import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { of, timer } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { TodosAdapter } from '../../adapters/todos.adapter';
import {
  loadTodosLazy,
  loadTodosPolling,
  loadTodosFailure,
  toggleTodoState,
  toggleTodoStateSuccess,
  markAllTodosAsDone,
  markAllTodosAsDoneSuccess,
  markAllTodosAsDoneFailure,
  deleteTodo,
  deleteTodoFailure,
  deleteTodoSuccess,
  toggleTodoStateFailure,
  deleteAllTodos,
  deleteAllTodosSuccess,
  deleteAllTodosFailure,
  loadTodosLazySuccess,
  loadTodosPollingSuccess,
} from './todos.actions';
import { Action, createAction, Store } from '@ngrx/store';
import { AppState } from '../../app.reducer';
import { selectTodosLazyParameters, selectTodosPollingParameters } from './todos.reducer';
import { selectLoggedIn } from '../auth/auth.reducer';
import { concatLatestFrom } from '@ngrx/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export const TodosEffectInit = createAction('[Todo/Effects] Todo Effect Initialized');

@UntilDestroy()
@Injectable()
export class TodosEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly todosAdapter = inject(TodosAdapter);
  private readonly store = inject(Store<AppState>);
  
  private readonly INTERVAL: number = 60000;

  ngrxOnInitEffects(): Action {
    timer(0, this.INTERVAL)
      .pipe(
        concatLatestFrom(() => this.store.select(selectLoggedIn)),
        untilDestroyed(this)
      )
      .subscribe(([, loggedIn]) => {
        if (loggedIn) {
          this.store.dispatch(loadTodosPolling());
        }
      });
    return TodosEffectInit();
  }

  loadTodosLazy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTodosLazy),
      concatLatestFrom(() => this.store.select(selectTodosLazyParameters)),
      exhaustMap(([, parameters]) =>
        this.todosAdapter.getTodos(...Object.values(parameters)).pipe(
          map((response) => loadTodosLazySuccess(response)),
          catchError((error) => {
            return of(loadTodosFailure({ error }));
          })
        )
      )
    )
  );

  loadTodosPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadTodosPolling),
      concatLatestFrom(() => this.store.select(selectTodosPollingParameters)),
      exhaustMap(([, parameters]) =>
        this.todosAdapter.getTodos(...Object.values(parameters)).pipe(
          map((response) => loadTodosPollingSuccess(response)),
          catchError((error) => {
            return of(loadTodosFailure({ error }));
          })
        )
      )
    )
  );

  toggleTodoState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleTodoState),
      mergeMap((props) =>
        this.todosAdapter.toggleState(props.todo).pipe(
          map(() => toggleTodoStateSuccess({ todo: props.todo })),
          catchError((error) => {
            return of(toggleTodoStateFailure({ error }));
          })
        )
      )
    )
  );

  deleteTodo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteTodo),
      mergeMap((props) =>
        this.todosAdapter.deleteTodo(props.todo).pipe(
          map(() => deleteTodoSuccess({ todo: props.todo })),
          catchError((error) => {
            return of(deleteTodoFailure({ error }));
          })
        )
      )
    )
  );

  deleteAllTodos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteAllTodos),
      mergeMap(() =>
        this.todosAdapter.deleteAll().pipe(
          mergeMap(() => [deleteAllTodosSuccess(), loadTodosLazy()]),
          catchError((error) => {
            return of(deleteAllTodosFailure({ error }));
          })
        )
      )
    )
  );

  loadTodosFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadTodosFailure),
        tap((action) => {
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );

  markAllTodosAsDone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markAllTodosAsDone),
      mergeMap((props) =>
        this.todosAdapter.markAllAsDone().pipe(
          map(() => markAllTodosAsDoneSuccess()),
          catchError((error) => {
            return of(markAllTodosAsDoneFailure({ error }));
          })
        )
      )
    )
  );
}
